import { DerbyRngV1 } from '../rng';
import { DRILL_TICK_S, MASK_HELD, type MinigameDef, type MinigameSession, type SessionScore } from './framework';

export interface HillSegment { startTick: number; endTick: number; climb: boolean }

/** Stamina repeats: spend effort on the climbs and earn it back downhill. */
export class HillsSession implements MinigameSession {
  readonly durationTicks = Math.round(15 / DRILL_TICK_S);
  readonly segments: HillSegment[];
  tick = 0;
  effort = 1;
  blown = false;
  private climbWork = 0;
  private climbTicks = 0;
  private recoveryDiscipline = 0;
  private descentTicks = 0;

  constructor(readonly tier: number, seed: number) {
    const rng = new DerbyRngV1(seed).fork('hills');
    const segments: HillSegment[] = [];
    let at = 0;
    // Put the first decision on screen immediately; recovery follows work.
    let climb = true;
    while (at < this.durationTicks) {
      const seconds = climb ? rng.range(1.8, 2.6) : rng.range(1.1, 1.7);
      const endTick = Math.min(this.durationTicks, at + Math.round(seconds / DRILL_TICK_S));
      segments.push({ startTick: at, endTick, climb });
      at = endTick;
      climb = !climb;
    }
    this.segments = segments;
  }

  get finished(): boolean { return this.blown || this.tick >= this.durationTicks; }
  segmentNow(): HillSegment { return this.segments.find((s) => s.startTick <= this.tick && this.tick < s.endTick) ?? this.segments.at(-1)!; }

  step(mask: number): void {
    if (this.finished) return;
    const held = (mask & MASK_HELD) !== 0;
    if (this.segmentNow().climb) {
      this.climbTicks += 1;
      if (held && this.effort > 0) {
        this.climbWork += 1;
        this.effort -= (0.13 + this.tier * 0.008) * DRILL_TICK_S;
      } else {
        this.effort = Math.min(1, this.effort + 0.025 * DRILL_TICK_S);
      }
    } else {
      this.descentTicks += 1;
      if (!held) {
        this.recoveryDiscipline += 1;
        this.effort = Math.min(1, this.effort + 0.22 * DRILL_TICK_S);
      } else {
        this.effort -= 0.05 * DRILL_TICK_S;
      }
    }
    if (this.effort <= 0) { this.effort = 0; this.blown = true; }
    this.tick += 1;
  }

  score(): SessionScore {
    const work = this.climbTicks ? this.climbWork / this.climbTicks : 0;
    const recover = this.descentTicks ? this.recoveryDiscipline / this.descentTicks : 0;
    const raw = Math.max(0, Math.min(1, 0.7 * work + 0.2 * recover + 0.1 * this.effort));
    return { raw, detail: `${Math.round(work * 100)}% climb work · ${Math.round(this.effort * 100)}% effort left${this.blown ? ' · emptied out' : ''}`, failed: this.blown };
  }
}

export const HILLS_DEF: MinigameDef = {
  id: 'hills', name: 'Hill Repeats', stat: 'stamina', energyCost: 4,
  create: (tier, seed) => new HillsSession(tier, seed),
};
